import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "company-information" }
const _hoisted_2 = { class: "company-information__columns" }
const _hoisted_3 = { class: "company-information__address-card-header" }

import { ref, computed, inject, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { onBeforeRouteLeave } from 'vue-router';
import { Translator } from 'types/injects/translator.type';
import { PushToast } from 'types/injects/pushToast.type';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import CompanyInformationLoader from 'vcomponents/preferences/CompanyInformation/CompanyInformationLoader.vue';
import CompanyInformationFormLocked from 'vcomponents/preferences/CompanyInformation/CompanyInformationFormLocked.vue';
import CompanyInformationForm from 'vcomponents/preferences/CompanyInformation/CompanyInformationForm.vue';
import CompanyContactForm from 'vcomponents/preferences/CompanyInformation/CompanyContactForm.vue';
import CompanyInformationAddressList from 'vcomponents/preferences/CompanyInformation/CompanyInformationAddressList.vue';
import { getAccountInformation } from 'api/account/account';
import { getCountries } from 'api/countries';
import { fetchAccountAddresses } from 'api/addressesService';

export interface LegalFrance {
    siren: string;
    siret: string;
    vat: string;
    company_type: string;
    ape_naf_code: string;
    rcs_immatriculation: string;
}

export interface CompanyData {
    country_code: string;
    legal_name: string;
    capital: string;
    legal_france: LegalFrance;
}

export interface Country {
    code: string;
    name: string;
}

type Address = Record<string, unknown>;


export default /*@__PURE__*/_defineComponent({
  __name: 'CompanyInformation',
  setup(__props) {

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const store = useStore();

const companyInformation = ref<CompanyData>({} as CompanyData);
const countries = ref<Country[]>([]);
const addresses = ref<Address[]>([]);
const isLoading = ref<boolean>(true);
const isPanelOpen = ref<boolean>(false);

const companyInformationLocked = computed(
    (): boolean => store.getters['corp/getCompanyInformationLocked'],
);

const handleCompanyInformationUpdate = (payload: CompanyData): void => {
    companyInformation.value = payload;
};

const refreshAddresses = (newAddresses: Address[]): void => {
    addresses.value = newAddresses;
};

const handleAddAddress = (): void => {
    isPanelOpen.value = true;
    store.dispatch('popupPanel/open', {
        key: 'addressForm',
        props: {
            address: null,
            isAccountAddress: true,
            callback: (): void => {
                isPanelOpen.value = false;
            },
        },
    });
};

onBeforeRouteLeave((): void => {
    store.dispatch('popupPanel/close', { key: 'addressForm' });
});
onMounted((): void => {
    const accountPromise = getAccountInformation()
        .then((response) => {
            companyInformation.value = response.data as CompanyData;
        })
        .catch((): void => {
            $pushToast({
                type: 'error',
                text: $t.__('CompanyInformation.fetch.error'),
            });
        });

    const countriesPromise = getCountries()
        .then((response) => {
            countries.value = (response.data as { data: Country[] }).data;
        })
        .catch((): void => {
            $pushToast({
                type: 'error',
                text: $t.__('CompanyInformation.countries.fetch.error'),
            });
        });

    const addressesPromise = fetchAccountAddresses()
        .then((response) => {
            addresses.value = response as Address[];
        })
        .catch((): void => {
            $pushToast({
                type: 'error',
                text: $t.__('CompanyInformation.addresses.fetch_error'),
            });
        });

    Promise.all([accountPromise, countriesPromise, addressesPromise]).finally((): void => {
        isLoading.value = false;
    });

    EventBus.on('add-address', ((item: Address): void => {
        addresses.value.push(item);
    }) as (...args: unknown[]) => void);

    EventBus.on('edit-address', ((address: Address): void => {
        addresses.value = addresses.value.map((item) => {
            if (item['id'] !== address['id']) {
                return item;
            }
            return { ...item, ...address };
        });
    }) as (...args: unknown[]) => void);
});

onBeforeUnmount((): void => {
    EventBus.off('add-address');
    EventBus.off('edit-address');
});

return (_ctx: any,_cache: any) => {
  const _component_CompassButton = _resolveComponent("CompassButton")!
  const _component_CompassAlert = _resolveComponent("CompassAlert")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(Card, null, {
        default: _withCtx(() => [
          _createVNode(Heading, {
            labelTitle: _unref($t).__('CompanyInformation.legal_information.title'),
            variant: "h2",
            prefixColoredIcon: { icon: 'building-skyscraper', color: 'brand' }
          }, null, 8 /* PROPS */, ["labelTitle"]),
          (isLoading.value)
            ? (_openBlock(), _createBlock(CompanyInformationLoader, { key: 0 }))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (companyInformationLocked.value)
                  ? (_openBlock(), _createBlock(CompanyInformationFormLocked, {
                      key: 0,
                      data: companyInformation.value,
                      countries: countries.value,
                      onUpdate: handleCompanyInformationUpdate
                    }, null, 8 /* PROPS */, ["data", "countries"]))
                  : (_openBlock(), _createBlock(CompanyInformationForm, {
                      key: 1,
                      data: companyInformation.value,
                      countries: countries.value,
                      onUpdate: handleCompanyInformationUpdate
                    }, null, 8 /* PROPS */, ["data", "countries"]))
              ], 64 /* STABLE_FRAGMENT */))
        ]),
        _: 1 /* STABLE */
      }),
      _createVNode(Card, null, {
        default: _withCtx(() => [
          _createVNode(Heading, {
            labelTitle: _unref($t).__('CompanyInformation.contact_information.title'),
            variant: "h2",
            prefixColoredIcon: { icon: 'user-hexagon', color: 'brand' }
          }, null, 8 /* PROPS */, ["labelTitle"]),
          (isLoading.value)
            ? (_openBlock(), _createBlock(CompanyInformationLoader, { key: 0 }))
            : (_openBlock(), _createBlock(CompanyContactForm, {
                key: 1,
                data: companyInformation.value
              }, null, 8 /* PROPS */, ["data"]))
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _createVNode(Card, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(Heading, {
            labelTitle: _unref($t).__('CompanyInformation.addresses.title'),
            variant: "h2",
            prefixColoredIcon: { icon: 'location', color: 'brand' }
          }, null, 8 /* PROPS */, ["labelTitle"]),
          _createVNode(_component_CompassButton, {
            label: _unref($t).__('CompanyInformation.addresses.button'),
            type: "secondary",
            disabled: isPanelOpen.value,
            onClick: handleAddAddress
          }, null, 8 /* PROPS */, ["label", "disabled"])
        ]),
        _createVNode(_component_CompassAlert, {
          type: "neutral",
          description: _unref($t).__('CompanyInformation.addresses.alert'),
          customIcon: "/ressources/logged/img/icon/svg/info-e-invoicing.svg",
          showClose: false
        }, null, 8 /* PROPS */, ["description"]),
        _createVNode(CompanyInformationAddressList, {
          addresses: addresses.value,
          loading: isLoading.value,
          isPanelOpen: isPanelOpen.value,
          onDelete: refreshAddresses,
          onUpdate: refreshAddresses,
          onPanelOpen: _cache[0] || (_cache[0] = ($event: any) => (isPanelOpen.value = true)),
          onPanelClose: _cache[1] || (_cache[1] = ($event: any) => (isPanelOpen.value = false))
        }, null, 8 /* PROPS */, ["addresses", "loading", "isPanelOpen"])
      ]),
      _: 1 /* STABLE */
    })
  ]))
}
}

})